// The Sales home's metric row (GET /api/sales/pulse): headline numbers, each a
// card that opens the list behind it, filtered the same way it was counted.
import { linkToFilters } from '../objects/listState';
import { ObjectsApiError } from '../objects/objectsApi';

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

export interface SalesPulse {
  open_count: number;
  open_amount: number;
  weighted_amount: number;
  closing_count: number;
  closing_amount: number;
  won_count: number;
  won_amount: number;
  past_due_count: number;
  past_due_amount: number;
  won_90: number;
  lost_90: number;
  revenue_amount: number;
  new_leads: number;
  open_leads: number;
}

/** A metric: what it counts, its number, a line under it, and the list it opens. */
export interface PulseTile {
  key: string;
  label: string;
  value: string;
  caption: string;
  href: string;
}

export async function fetchSalesPulse(fetcher: Fetch): Promise<SalesPulse> {
  const response = await fetcher('/api/sales/pulse');
  if (!response.ok) throw new ObjectsApiError(response.status, 'Could not load the sales numbers.');
  return (await response.json()) as SalesPulse;
}

const deals = (n: number) => `${n} ${n === 1 ? 'deal' : 'deals'}`;
const row = (field: string, operator: 'equals' | 'not_equals' | 'less_than', value: string) => ({ field, operator, value });
const open = [row('stage', 'not_equals', 'closed_won'), row('stage', 'not_equals', 'closed_lost')];

/** "$168k", "$52.5k", "$1.2M", "$800": figures on the home page. */
export function k(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `$${(n / 1e3).toFixed(abs >= 1e5 ? 0 : 1)}k`;
  return `$${Math.round(n)}`;
}

/** The six metrics, in reading order: pipeline, its timing, results, revenue, then the top of the funnel. */
export function pulseTiles(p: SalesPulse): PulseTile[] {
  const decided = p.won_90 + p.lost_90;
  return [
    {
      key: 'weighted',
      label: 'Weighted pipeline',
      value: k(p.weighted_amount),
      caption: `${k(p.open_amount)} open · ${deals(p.open_count)}`,
      href: linkToFilters('/opportunities', { view: 'open', rows: open }),
    },
    {
      key: 'closing',
      label: 'Closing this month',
      value: k(p.closing_amount),
      caption: deals(p.closing_count),
      href: linkToFilters('/opportunities', { view: 'open', rows: [...open, row('close_date', 'equals', 'THIS_MONTH')] }),
    },
    {
      key: 'won',
      label: 'Won this month',
      value: k(p.won_amount),
      caption: deals(p.won_count),
      href: linkToFilters('/opportunities', { view: 'won', rows: [row('stage', 'equals', 'closed_won'), row('close_date', 'equals', 'THIS_MONTH')] }),
    },
    {
      key: 'revenue',
      label: 'Revenue this month',
      value: k(p.revenue_amount),
      caption: 'scheduled from won deals',
      href: linkToFilters('/revenue', { rows: [row('period_start', 'equals', 'THIS_MONTH'), row('stage', 'equals', 'closed_won')] }),
    },
    {
      key: 'win-rate',
      label: 'Win rate',
      value: decided ? `${Math.round((p.won_90 / decided) * 100)}%` : '—',
      caption: `${p.won_90} won · ${p.lost_90} lost · 90 days`,
      href: linkToFilters('/opportunities', {
        view: 'all',
        rows: [row('close_date', 'equals', 'LAST_N_DAYS:90'), row('stage', 'equals', 'closed_won'), row('stage', 'equals', 'closed_lost')],
        logic: '1 AND (2 OR 3)',
      }),
    },
    {
      key: 'leads',
      label: 'New leads',
      value: String(p.new_leads),
      caption: 'last 30 days',
      href: linkToFilters('/leads', { view: 'all', rows: [row('created_at', 'equals', 'LAST_N_DAYS:30')] }),
    },
  ];
}
